﻿import React, {HTMLInputAutoCompleteAttribute, JSX, MouseEvent, useCallback, useState} from "react";
import MaterialIcon from "../MaterialIcon";
import {HtmlRequiredPrefixedControlProps} from "./HtmlControlProps";
import LabeledControl from "./LabeledControl";
import {useLabeledControl} from "./LabeledControlData";

export type HtmlInputType =
	"text"
	| "color"
	| "date"
	| "datetime-local"
	| "email"
	| "month"
	| "number"
	| "password"
	| "search"
	| "tel"
	| "time"
	| "url"
	| "week";

export type InputProps = HtmlRequiredPrefixedControlProps<HTMLInputElement> & {
	type: HtmlInputType;
	max?: number | string;
	min?: number | string;
	maxLength?: number;
	minLength?: number;
	step?: number;
	placeholder?: string;

	autoComplete?: HTMLInputAutoCompleteAttribute;
	autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters" | undefined | (string & {});
	enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send" | undefined;
	inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search" | undefined;
	autoFocus?: boolean;
};

export default function Input(props: InputProps): JSX.Element {
	const {
		label,
		name,
		value,
		id,
		column,
		disabled,
		hidden,
		onChange,
		onBlur,
		ref,

		required,
		hint,
		error,

		controlPrefix,
		controlSuffix,

		type,
		max,
		min,
		maxLength,
		minLength,
		step,
		placeholder,
		autoComplete,
		autoCapitalize,
		enterKeyHint,
		inputMode,
		autoFocus
	} = props;

	const isPassword = type === 'password';
	const [inputType, setInputType] = useState(type);

	const {componentId, ariaRef} = useLabeledControl(id);

	const togglePasswordVisibility = useCallback(function (event: MouseEvent<HTMLButtonElement>): void {
		event.preventDefault();
		setInputType(inputType === 'password' ? 'text' : 'password');
	}, [inputType]);

	if (hidden) {
		return <></>;
	}

	return (
		<LabeledControl label={label} type={inputType} id={componentId} required={required} column={column}
		                error={error}
		                hint={hint} hidden={hidden} ariaRef={ariaRef} controlPrefix={controlPrefix}
		                controlSuffix={controlSuffix}>
			<input
				id={componentId}
				type={inputType}
				ref={ref}
				onChange={onChange}
				onBlur={onBlur}
				name={name}
				value={onChange == null ? undefined : value}
				defaultValue={onChange == null ? value : undefined}
				max={max}
				min={min}
				step={step}
				required={required}
				maxLength={maxLength}
				minLength={minLength}
				placeholder={placeholder}
				autoCapitalize={autoCapitalize}
				autoComplete={autoComplete}
				enterKeyHint={enterKeyHint}
				inputMode={inputMode}
				autoFocus={autoFocus}
				readOnly={disabled}
				aria-describedby={ariaRef.current?.describedBy}
				aria-invalid={ariaRef.current?.invalid}
				aria-required={required}
				tabIndex={disabled ? -1 : undefined}
			/>
			{isPassword && <button type="button" onClick={togglePasswordVisibility}
                                   className="field__input__password-toggle" tabIndex={-1}>
                <MaterialIcon icon={inputType === 'password' ? 'visibility' : 'visibility_off'}/>
            </button>}
		</LabeledControl>
	);
};
